import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPermission } from '@auth';
import {
  BottomSheet,
  Button,
  DeleteConfirmationBottomSheet,
  DeleteErrorBottomSheet,
  Modal
} from '@basic';
import { UIPaths } from '@consts';
import CoffeeIcon from '@icons/CoffeeIcon';
import EditIcon from '@icons/EditIcon';
import MountainWithFlagIcon from '@icons/MountainWithFlagIcon';
import SkillGraphIcon from '@icons/SkillGraphIcon';
import SocialIcon from '@icons/SocialIcon';
import XCircleIcon from '@icons/XCircleIcon';
import { GoalModel, GoalType, PermissionEntityType, PermissionType } from '@models';
import { useStore } from '@store';
import { setupForDark } from '@theme';

const GoalBottomSheet = ({
  goal,
  isOpen = false,
  onClose = () => {},
  position
}: {
  goal: GoalModel;
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [unsubscribeModalIsOpen, setUnsubscribeModalIsOpen] = useState(false);
  const [deleteConfirmationModalIsOpen, setDeleteConfirmationModalIsOpen] = useState(false);
  const [deleteFailedModalState, setDeleteFailedModalState] = useState({
    isOpen: false,
    error: null
  });
  const { createGoalSubscription, updateGoalSubscription, getGoal, deleteGoal } = useStore(
    (state) => ({
      createGoalSubscription: state.createGoalSubscription,
      updateGoalSubscription: state.updateGoalSubscription,
      getGoal: state.getGoalOld,
      deleteGoal: state.deleteGoal
    })
  );

  const goalEditPermission = useMemo(() => {
    return getPermission(goal.__permissions, PermissionType.EDIT, PermissionEntityType.GOAL);
  }, [goal.__permissions]);

  const goalSubscribePermission = useMemo(() => {
    return getPermission(goal.__permissions, PermissionType.SUBSCRIBE, PermissionEntityType.GOAL);
  }, [goal]);

  const onGoToGoalEditor = (e) => {
    if (!goalEditPermission) return;
    e.preventDefault();
    e.stopPropagation();
    router.push(UIPaths.goalEditor(goalEditPermission.entityId as string));

    onClose();
  };

  const onGoToGoalUsers = (e) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(UIPaths.goalUsers(goal.entityId as string));
    onClose();
  };

  const onGoToSkillGraphEditor = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!goal.skillGraph?.entityId) return;
    router.push(UIPaths.skillGraphEditor(goal.skillGraph.entityId));
    onClose();
  };

  const onSubscribe = (event) => {
    event.preventDefault();

    createGoalSubscription(goal.entityId, {
      goalEntityId: goal.entityId,
      priority: 'MEDIUM'
    });

    onClose();
  };

  const onPause = (event) => {
    event.preventDefault();

    updateGoalSubscription(goal.entityId, goal.subscription.id, {
      status: 'PAUSED'
    }).then(() => {
      getGoal(goal?.entityId.toString());
    });

    onClose();
    setUnsubscribeModalIsOpen(false);
  };

  const onResume = (event) => {
    event.preventDefault();

    createGoalSubscription(goal.entityId, { goalEntityId: goal.entityId }).then(() => {
      getGoal(goal?.entityId.toString());
    });
    onClose();
  };

  // TODO: Wont be dead code after deleting goal will be available
  const onDeleteClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onClose();
    setDeleteConfirmationModalIsOpen(true);
  };

  const onDeleteFailed = (error) => {
    setDeleteFailedModalState({
      isOpen: true,
      error: error
    });
  };

  const onCloseDeleteFailedModal = () => {
    setDeleteFailedModalState({
      isOpen: false,
      error: null
    });
  };

  const onUnsubscribe = (event) => {
    event.preventDefault();
    setUnsubscribeModalIsOpen(true);
    onClose();
  };

  const onUnsubscribeConfirm = async (event) => {
    event.stopPropagation();
    await updateGoalSubscription(goal.entityId, goal.subscription.id, {
      status: 'DELETED'
    });
    await getGoal(goal?.entityId.toString());
    setUnsubscribeModalIsOpen(false);
  };

  const entryClass = classNames(
    'flex flex-row px-sm py-md gap-sm items-center cursor-pointer',
    setupForDark('text-secondary')
  );

  let modalText = '';
  let isPaused = false;
  if (goal.subscription && goal.subscription.status === 'ACTIVE') {
    modalText = t('components.goalUnsubscribeModal.textWhenActive');
  } else if (goal.subscription && goal.subscription.status === 'PAUSED') {
    isPaused = true;
    modalText = t('components.goalUnsubscribeModal.textWhenPaused');
  }

  return (
    <>
      {/* Ellipsis bottom sheet */}
      <BottomSheet
        isOpen={isOpen && !unsubscribeModalIsOpen}
        onClose={onClose}
        desktopMenuPosition={position}
      >
        <div className='flex flex-col px-lg'>
          {goalSubscribePermission && !goal.subscription && goal.type !== GoalType.COMBO && (
            <div className={entryClass} onClick={onSubscribe}>
              <MountainWithFlagIcon size={20} />
              <p className='P4M'>{t('components.goalBottomSheet.subscribeGoal')}</p>
            </div>
          )}
          {goalSubscribePermission &&
            !goal.subscription &&
            goal.type === GoalType.COMBO &&
            goal.externalSaleUrl && (
              <div
                className={entryClass}
                data-testid='goal-bottom-sheet-external-sale'
                onClick={() => {
                  window.location.href = goal.externalSaleUrl!;
                }}
              >
                <MountainWithFlagIcon size={20} />
                <p className='P4M'>{t('domains.goalSubscriptions.externalSaleDescription')}</p>
              </div>
            )}

          {/* FIXME Enable after https://app.clickup.com/t/8696e47wx is ready */}
          {/* {goal.subscription && goal.subscription.status === 'ACTIVE' && (
            <div className={entryClass} onClick={onPause}>
              <CoffeeIcon size={20} />
              <p className='P4M'>{t('components.goalBottomSheet.pauseGoal')}</p>
            </div>
          )} */}

          {goal.subscription && goal.subscription.status === 'PAUSED' && (
            <div className={entryClass} onClick={onResume}>
              <CoffeeIcon size={20} />
              <p className='P4M'>{t('components.goalBottomSheet.resumeGoal')}</p>
            </div>
          )}

          {goalEditPermission && (
            <>
              <div className={entryClass} onClick={onGoToGoalEditor}>
                <EditIcon size={20} />
                <p className='P4M'>{t('components.goalBottomSheet.goToGoalEditor')}</p>
              </div>
              {goal.skillGraph?.entityId && (
                <div className={entryClass} onClick={onGoToSkillGraphEditor}>
                  <SkillGraphIcon size={20} />
                  <p className='P4M'>{t('components.goalBottomSheet.goToSkillGraphEditor')}</p>
                </div>
              )}
              <div className={entryClass} onClick={onGoToGoalUsers}>
                <SocialIcon size={20} />
                <p className='P4M'>{t('components.goalBottomSheet.goToGoalUsers')}</p>
              </div>
              {/* #FIXME: enable once deleting goal will be available*/}
              {/* <div className={entryClass} onClick={onDeleteClicked}>
                <TrashCanIcon size={20} />
                <p className='P4M'>{t('components.goalBottomSheet.deleteGoal')}</p>
              </div> */}
            </>
          )}

          {goal.subscription && (
            <div className={entryClass} onClick={onUnsubscribe}>
              <XCircleIcon size={20} />
              <p className='P4M'>{t('components.goalBottomSheet.unsubscribeGoal')}</p>
            </div>
          )}
        </div>
      </BottomSheet>
      {/* Unsubscribe modal */}
      <Modal isOpen={unsubscribeModalIsOpen} onClose={() => setUnsubscribeModalIsOpen(false)}>
        <div
          className={classNames(
            'flex flex-row items-center justify-center gap-md mb-xl',
            setupForDark('text-secondary')
          )}
        >
          <XCircleIcon size={48} />
        </div>
        <div className={classNames('H5 text-center', setupForDark('text-primary'))}>
          {t('components.goalUnsubscribeModal.title')}
        </div>
        <div className={classNames('P2M mt-xxl mb-xl text-center', setupForDark('text-secondary'))}>
          {modalText}
        </div>
        <div className='flex flex-row items-center justify-center gap-md'>
          <Button size='small' type='tertiary' onClick={() => setUnsubscribeModalIsOpen(false)}>
            {t('components.goalUnsubscribeModal.closeButton')}
          </Button>
          {/* FIXME Enable after https://app.clickup.com/t/8696e47wx is ready */}
          {/* {!isPaused && (
            <Button size='small' type='secondary' onClick={onPause}>
              {t('components.goalBottomSheet.pauseGoal')}
            </Button>
          )} */}
          <Button size='small' type='secondary' onClick={onUnsubscribeConfirm}>
            {t('components.goalBottomSheet.unsubscribeGoal')}
          </Button>
        </div>
      </Modal>
      {/* Delete confirmation modal */}
      <DeleteConfirmationBottomSheet
        isOpen={deleteConfirmationModalIsOpen}
        onClose={() => setDeleteConfirmationModalIsOpen(false)}
        deleteConfirmationHeader={t('components.goalBottomSheet.deleteGoalConfirmationHeader')}
        deleteConfirmationText={t('components.goalBottomSheet.deleteGoalConfirmationText')}
        onDeleteSucceeded={() => setDeleteConfirmationModalIsOpen(false)}
        onDeleteFailed={onDeleteFailed}
        deleteFunction={deleteGoal.bind(null, goal.entityId)}
      />
      {/* Delete failed modal */}
      <DeleteErrorBottomSheet
        isOpen={deleteFailedModalState.isOpen}
        onClose={onCloseDeleteFailedModal}
        error={deleteFailedModalState.error}
      />
    </>
  );
};

export default GoalBottomSheet;
